import { Badge, Box, Flex, Heading, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  type MinimumRequirementDto,
  type PortalSourcingEventDetailDto,
  type PublicBidDto,
  type PublicBidPatchDto,
  type SupplierPublicBidMinimumRequirementResponseDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import {
  MinimumRequirementAnswerCard,
  isMinimumRequirementResponseComplete,
} from "../MinimumRequirementAnswerCard";
import { isPublicBidEditable } from "../publicBidEditability";

interface Props {
  bidId: string;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

// t('Minimum requirements') t('Buyer criteria')
// t('Answer the buyer's minimum requirements.')

export const MinimumRequirementsStep = ({ bidId, event, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<PublicBidDto, PublicBidPatchDto>(bidId);
  const readOnly = !isPublicBidEditable(state);

  const requirements = event.minimumRequirements ?? [];
  const responses = state.minimumRequirementResponses ?? [];

  const isSaving =
    fieldStatus("minimumRequirementResponses") === "saving" ||
    fieldStatus("minimumRequirementResponses") === "pending";

  const upsert = (
    requirement: MinimumRequirementDto,
    patch: Partial<SupplierPublicBidMinimumRequirementResponseDto>,
  ) => {
    const id = requirement.id ?? "";
    const current = responses.find((r) => r.minimumRequirementId === id);
    const next: SupplierPublicBidMinimumRequirementResponseDto = {
      minimumRequirementId: id,
      answer: current?.answer ?? false,
      comment: current?.comment ?? null,
      ...patch,
    };
    const nextResponses = [...responses.filter((r) => r.minimumRequirementId !== id), next];
    updateField({ minimumRequirementResponses: nextResponses }, { debounceMs: 300 });
  };

  const answeredCount = requirements.filter((r) =>
    isMinimumRequirementResponseComplete(r, responses.find((response) => response.minimumRequirementId === (r.id ?? ""))),
  ).length;

  return (
    <Box pb="8" pt="6">
      <Flex align="center" justify="space-between" gap="3" mb="2" flexWrap="wrap">
        <HStack spacing="2" align="center" flexWrap="wrap">
          <Heading size="md">{t("Minimum requirements")}</Heading>
          <Badge bg="smPrimarySofter" color="smPrimary" px="2" py="0.5" borderRadius="md" textTransform="none">
            {t("Buyer criteria")}
          </Badge>
          {isSaving && <Spinner size="sm" color="smMuted" />}
        </HStack>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={requirements.length > 0 && answeredCount === requirements.length ? "smSuccess" : "smMuted"}
        >
          {t("{{answered}} of {{total}} answered", { answered: answeredCount, total: requirements.length })}
        </Text>
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="6">
        {t("Answer the buyer's minimum requirements.")}
      </Text>

      <Stack spacing="3" mb="6">
        {requirements.map((requirement) => {
          const id = requirement.id ?? "";
          const response = responses.find((r) => r.minimumRequirementId === id);

          return (
            <MinimumRequirementAnswerCard
              key={id}
              requirement={requirement}
              response={response}
              isReadOnly={readOnly}
              onAnswerChange={(answer) =>
                upsert(requirement, answer ? { answer: true, comment: null } : { answer: false })
              }
              onCommentChange={(comment) => upsert(requirement, { comment })}
            />
          );
        })}
      </Stack>

      <Box mt="8">
        <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
      </Box>
    </Box>
  );
};
